import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Breadcrumb,
  Row,
  Col,
  Typography,
  Tag,
  Spin,
  Alert,
  Drawer,
  message,
  Table,
  Input,
  Space,
  Tabs,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  HomeOutlined,
  UserOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  DisconnectOutlined,
  PhoneOutlined,
  TeamOutlined,
  FileTextOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import { customerAPIWithErrorHandling } from "../../../api/customer/customer";
import UploadModal from "../../../components/Upload/UploadModal";
import { settings } from "../../../utils/config";
import RowActions from "../../../components/common/RowActions";
import StatCard from "../../../components/common/StatCard";
import { confirmAction } from "../../../components/common/confirmDialog";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import { border, brand, neutral, radius, shadow, spacing } from "../../../theme/tokens";

const { Title, Text } = Typography;

const containerStyle = {
  backgroundColor: "#F5F7FA",
  minHeight: "100vh",
  padding: "15px",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "24px",
  padding: "24px",
  backgroundColor: neutral[0],
  borderRadius: radius.md,
  boxShadow: shadow.xs,
  border: border.hairline,
  flexWrap: "wrap",
  gap: "16px",
};

const userInfoStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flex: 1,
  minWidth: "250px",
};

const titleStyle = {
  marginBottom: "4px",
  color: "#1E293B",
  fontWeight: "600",
  fontSize: "24px",
};

const codeStyle = {
  fontSize: "14px",
  color: "#64748B",
};

const codeValueStyle = {
  color: brand.accent,
  fontWeight: "600",
  backgroundColor: brand.primarySubtle,
  padding: "2px 8px",
  borderRadius: "4px",
};

const cardStyle = {
  boxShadow: shadow.xs,
  borderRadius: radius.md,
  border: border.hairline,
  overflow: "hidden",
};

/** Số tiền hiển thị dạng 1,234,567 */
const formatAmount = (value) =>
  `${value ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Nhãn trạng thái khoản thu. Dùng đúng chữ như màn sửa đợt thu và file Excel xuất ra —
// cùng một trạng thái mà mỗi nơi gọi một tên thì người đối chiếu số liệu phải đoán.
const FEE_STATUS_LABELS = {
  UNPAID: "Chưa thanh toán",
  PARTIAL: "Đã thu một phần",
  PAID: "Đã thanh toán",
  CANCELLED: "Đã hủy",
};

const FEE_STATUS_COLORS = {
  UNPAID: "orange",
  PARTIAL: "blue",
  PAID: "green",
  CANCELLED: "red",
};

// Custom Hook để quản lý table data
const useTableData = (fetchFunction, organizationId) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  const loadData = useCallback(
    async (params = {}) => {
      if (!organizationId) return;

      try {
        setLoading(true);
        const response = await fetchFunction(organizationId, {
          page: params.page || pagination.current,
          per_page: params.per_page || pagination.pageSize,
          search: params.search !== undefined ? params.search : searchText,
        });

        if (response.success) {
          const { data: responseData } = response;
          setData(responseData.data);
          setPagination({
            current: responseData.current_page,
            pageSize: params.per_page || pagination.pageSize,
            total: responseData.total,
          });
        }
      } catch (error) {
        message.error(error.message || "Không thể tải dữ liệu");
      } finally {
        setLoading(false);
      }
    },
    [fetchFunction, organizationId, pagination.current, pagination.pageSize, searchText]
  );

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
      loadData({ search: value, page: 1 });
    },
    [loadData]
  );

  const handleTableChange = useCallback(
    (newPagination) => {
      loadData({
        page: newPagination.current,
        per_page: newPagination.pageSize,
      });
    },
    [loadData]
  );

  return {
    data,
    loading,
    pagination,
    searchText,
    setData,
    loadData,
    handleSearch,
    handleTableChange,
  };
};


export default function OrganizationInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [activeTab, setActiveTab] = useState("customers");
  const [isBulkAddModalVisible, setIsBulkAddModalVisible] = useState(false);

  // Thêm khách hàng hàng loạt. Danh sách lấy theo trang từ backend: đơn vị này có
  // gần 6.000 cư dân, tải hết về rồi tự lọc vừa nặng vừa lọc sai (bản cũ chỉ trừ được
  // đúng trang khách hàng đang mở nên danh sách vẫn đầy người đã thuộc đơn vị).
  const [bulkCustomers, setBulkCustomers] = useState([]);
  const [loadingBulkCustomers, setLoadingBulkCustomers] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [bulkSearchText, setBulkSearchText] = useState("");
  const [bulkPagination, setBulkPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);

  // Gỡ hàng loạt: chọn theo checkbox trong bảng. Cố ý KHÔNG giữ lựa chọn khi đổi trang —
  // gỡ khỏi đơn vị là việc không quay lại được, người dùng cần thấy đúng những dòng
  // mình đang gỡ. Muốn dọn cả đơn vị thì đã có nút "Gỡ tất cả".
  const [selectedCustomerKeys, setSelectedCustomerKeys] = useState([]);
  const [bulkDetaching, setBulkDetaching] = useState(false);

  // Stats
  const [totalCollections, setTotalCollections] = useState({
    total_paid: 0,
    total_unpaid: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);
  // Tổng khách hàng của đơn vị — đếm riêng, KHÔNG lấy từ pagination của bảng.
  // Bảng đổi total theo từ khóa đang tìm, lấy theo đó thì thẻ thống kê tụt xuống số
  // kết quả lọc và người xem tưởng đơn vị chỉ còn ngần ấy cư dân.
  const [totalCustomers, setTotalCustomers] = useState(0);

  // Sử dụng custom hook cho customers
  const customersTable = useTableData(
    organizationAPIWithErrorHandling.getOrgCustomers,
    id
  );

  // Sử dụng custom hook cho collections
  const collectionsTable = useTableData(
    organizationAPIWithErrorHandling.getOrgCollections,
    id
  );

  // Fetch organization info
  const fetchOrganizationInfo = async (orgId) => {
    if (!orgId) {
      message.error("ID đơn vị không hợp lệ");
      return;
    }

    setLoadingInfo(true);
    try {
      const response =
        await organizationAPIWithErrorHandling.getOrganizationById(orgId);
      const organizationData = response.data;
      setOrganization(organizationData);
    } catch (error) {
      message.error(error.message || "Không thể tải thông tin");
      console.error("Error fetching organization:", error);
    } finally {
      setLoadingInfo(false);
    }
  };

  // Fetch total collections
  // Đếm khách hàng của đơn vị, không kèm từ khóa tìm kiếm. Xin đúng 1 bản ghi vì chỉ
  // cần con số `total` trong kết quả phân trang.
  const fetchCustomerCount = async (orgId) => {
    try {
      const res = await organizationAPIWithErrorHandling.getOrgCustomers(orgId, {
        page: 1,
        per_page: 1,
      });
      setTotalCustomers(res?.data?.total ?? 0);
    } catch (error) {
      // Thẻ thống kê hỏng không đáng làm gãy cả màn hình, giữ số cũ và đi tiếp
      console.error("Không đếm được khách hàng của đơn vị:", error);
    }
  };

  const fetchTotalCollection = async (orgId) => {
    try {
      setLoadingStats(true);
      const response =
        await organizationAPIWithErrorHandling.getTotalCollections(orgId);
      const priceData = response.data || {};
      setTotalCollections(priceData);
    } catch (error) {
      message.error("Không thể tải thông tin công nợ: " + error.message);
    } finally {
      setLoadingStats(false);
    }
  };

  // Tải danh sách khách hàng còn thêm được vào đơn vị, theo trang
  const fetchBulkCustomers = async ({ page, search } = {}) => {
    const nextPage = page ?? bulkPagination.current;
    const nextSearch = search !== undefined ? search : bulkSearchText;

    setLoadingBulkCustomers(true);
    try {
      const res = await organizationAPIWithErrorHandling.getAvailableCustomers(id, {
        page: nextPage,
        per_page: bulkPagination.pageSize,
        ...(nextSearch ? { search: nextSearch } : {}),
      });
      const paginator = res?.data || {};

      setBulkCustomers(paginator.data || []);
      setBulkPagination((prev) => ({
        ...prev,
        current: paginator.current_page || nextPage,
        total: paginator.total || 0,
      }));
    } catch (error) {
      message.error("Không thể tải danh sách khách hàng: " + error.message);
    } finally {
      setLoadingBulkCustomers(false);
    }
  };

  // Tìm kiếm ở backend, không lọc trên bản đã tải: danh sách chia trang nên lọc tại
  // chỗ chỉ soi được đúng trang đang mở.
  const handleBulkSearch = (value) => {
    setBulkSearchText(value);
    fetchBulkCustomers({ page: 1, search: value });
  };

  const handleBulkAdd = () => {
    setSelectedCustomerIds([]);
    setBulkSearchText("");
    setBulkPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    setIsBulkAddModalVisible(true);
    fetchBulkCustomers({ page: 1, search: "" });
  };

  /**
   * Báo kết quả theo con số backend trả về, không theo số đã chọn: người đã thuộc đơn
   * vị thì bị bỏ qua, báo theo số chọn là nói sai với người dùng.
   */
  const announceAttachResult = (result, requested) => {
    const attached = result?.attached ?? requested;
    const skipped = result?.skipped ?? 0;

    if (attached === 0) {
      message.info("Những khách hàng này đã thuộc đơn vị, không có gì để thêm");
      return;
    }

    message.success(
      skipped > 0
        ? `Đã thêm ${attached} khách hàng, bỏ qua ${skipped} người đã thuộc đơn vị`
        : `Đã thêm ${attached} khách hàng vào đơn vị`
    );
  };

  /** Làm mới màn hình sau khi thêm: cả bảng khách hàng lẫn danh sách còn thêm được. */
  const refreshAfterAttach = () => {
    customersTable.loadData();
    fetchOrganizationInfo(id);
    fetchTotalCollection(id);
      fetchCustomerCount(id);
  };

  const handleBulkAddSubmit = async () => {
    const requested = selectedCustomerIds.length;
    if (requested === 0) {
      message.warning("Vui lòng chọn ít nhất 1 khách hàng!");
      return;
    }

    setBulkSubmitting(true);
    try {
      const res = await organizationAPIWithErrorHandling.attachCustomers(id, {
        customer_ids: selectedCustomerIds,
      });

      announceAttachResult(res?.data, requested);
      setIsBulkAddModalVisible(false);
      setSelectedCustomerIds([]);
      refreshAfterAttach();
    } catch (error) {
      message.error("Không thể thêm khách hàng: " + error.message);
    } finally {
      setBulkSubmitting(false);
    }
  };

  /**
   * Thêm tất cả khách hàng còn thêm được theo đúng từ khóa đang lọc.
   *
   * Gửi cờ `all` kèm từ khóa thay vì gom id: màn hình chỉ nắm id của trang đang xem,
   * mà danh sách có thể tới vài nghìn người.
   */
  const handleBulkAddAll = async () => {
    const total = bulkPagination.total || 0;
    if (total === 0) return;

    const confirmed = await confirmAction({
      title: `Thêm toàn bộ ${total} khách hàng vào đơn vị?`,
      content: bulkSearchText
        ? `Thêm tất cả khách hàng khớp từ khóa "${bulkSearchText}", kể cả những trang chưa xem.`
        : "Thêm tất cả khách hàng chưa thuộc đơn vị này, kể cả những trang chưa xem.",
      okText: "Thêm tất cả",
    });
    if (!confirmed) return;

    setBulkSubmitting(true);
    try {
      const res = await organizationAPIWithErrorHandling.attachCustomers(id, {
        all: true,
        ...(bulkSearchText ? { search: bulkSearchText } : {}),
      });

      announceAttachResult(res?.data, total);
      setIsBulkAddModalVisible(false);
      setSelectedCustomerIds([]);
      refreshAfterAttach();
    } catch (error) {
      message.error("Không thể thêm khách hàng: " + error.message);
    } finally {
      setBulkSubmitting(false);
    }
  };

  // Xác nhận trước khi gỡ — dùng chung hộp thoại của hệ thống thay cho Popconfirm
  const confirmRemoveCustomer = async (record) => {
    const confirmed = await confirmAction({
      title: "Xác nhận gỡ khách hàng",
      content: `Khách hàng "${record.name || ""}" sẽ được gỡ khỏi đơn vị này, không bị xóa khỏi hệ thống. Bạn có chắc chắn không?`,
      okText: "Gỡ",
      okType: "danger",
    });
    if (!confirmed) return;

    handleRemoveCustomer(record.id);
  };

  // Handle remove customer with optimistic update
  const handleRemoveCustomer = async (customerId) => {
    // Optimistic update
    const previousData = [...customersTable.data];

    customersTable.setData((prev) => prev.filter((c) => c.id !== customerId));

    try {
      const payload = {
        customer_ids: [customerId],
      };

      await organizationAPIWithErrorHandling.detachCustomers(id, payload);
      message.success("Đã gỡ khách hàng khỏi đơn vị");
      // Dòng vừa gỡ mà còn nằm trong danh sách tích chọn thì thanh "Đã chọn N" sẽ đếm
      // một dòng không còn tồn tại.
      setSelectedCustomerKeys((prev) => prev.filter((key) => key !== customerId));

      // Reload để có data chính xác
      customersTable.loadData();
      
      fetchOrganizationInfo(id);
      fetchTotalCollection(id);
      fetchCustomerCount(id);
    } catch (error) {
      // Rollback on error
      customersTable.setData(previousData);
      message.error("Không thể gỡ khách hàng: " + error.message);
    }
  };

  /**
   * Gỡ các khách hàng đang tích chọn.
   *
   * Không dùng optimistic update như gỡ lẻ: gỡ cả chục dòng rồi lỡ lỗi mà phải dựng lại
   * bảng cũ thì dễ lệch với số liệu thật hơn là chờ đúng một nhịp tải lại.
   */
  const handleBulkDetach = async () => {
    const count = selectedCustomerKeys.length;
    if (count === 0) return;

    const confirmed = await confirmAction({
      title: `Gỡ ${count} khách hàng khỏi đơn vị?`,
      content:
        "Khách hàng chỉ rời khỏi đơn vị này, không bị xóa khỏi hệ thống. Khoản thu cũ vẫn nằm trong đợt thu nhưng sẽ không còn được tính vào thống kê và tab Phiếu thu của đơn vị.",
      okText: "Gỡ",
      okType: "danger",
    });
    if (!confirmed) return;

    setBulkDetaching(true);
    try {
      await organizationAPIWithErrorHandling.detachCustomers(id, {
        customer_ids: selectedCustomerKeys,
      });
      message.success(`Đã gỡ ${count} khách hàng khỏi đơn vị`);
      setSelectedCustomerKeys([]);

      // Gỡ sạch trang đang xem thì lùi một trang, không thì người dùng nhìn thấy bảng
      // rỗng trong khi đơn vị vẫn còn khách hàng ở các trang trước.
      const { current } = customersTable.pagination;
      const isPageEmptied = customersTable.data.length === count && current > 1;
      customersTable.loadData({ page: isPageEmptied ? current - 1 : current });

      fetchOrganizationInfo(id);
      fetchTotalCollection(id);
      fetchCustomerCount(id);
    } catch (error) {
      message.error("Không thể gỡ các khách hàng đã chọn: " + error.message);
    } finally {
      setBulkDetaching(false);
    }
  };

  /**
   * Gỡ toàn bộ khách hàng của đơn vị này.
   *
   * Gửi cờ `all` cho backend thay vì gom id từng trang: màn này chỉ nắm id của trang
   * đang xem, mà đơn vị đông cư dân thì có hàng nghìn liên kết.
   *
   * Đơn vị con không bị đụng tới — gỡ theo từng cấp để phạm vi luôn khớp với danh sách
   * người vận hành đang nhìn.
   */
  const handleDetachAll = async () => {
    const total = customersTable.pagination.total || 0;
    if (total === 0) return;

    const confirmed = await confirmAction({
      title: `Gỡ toàn bộ ${total} khách hàng khỏi đơn vị?`,
      content: `Tất cả khách hàng sẽ rời khỏi "${organization?.name || "đơn vị này"}", kể cả những trang chưa xem. Đơn vị con không bị ảnh hưởng. Khách hàng không bị xóa khỏi hệ thống, nhưng muốn quay lại thì phải gán lại từ đầu.`,
      okText: "Gỡ tất cả",
      okType: "danger",
    });
    if (!confirmed) return;

    setBulkDetaching(true);
    try {
      const res = await organizationAPIWithErrorHandling.detachCustomers(id, {
        all: true,
      });
      const detached = res?.data?.detached ?? total;

      message.success(`Đã gỡ ${detached} khách hàng khỏi đơn vị`);
      setSelectedCustomerKeys([]);
      customersTable.loadData({ page: 1 });

      fetchOrganizationInfo(id);
      fetchTotalCollection(id);
      fetchCustomerCount(id);
    } catch (error) {
      message.error("Không thể gỡ toàn bộ khách hàng: " + error.message);
    } finally {
      setBulkDetaching(false);
    }
  };

  // Đổi trang hoặc đổi từ khóa thì bỏ chọn. Lựa chọn không đi theo sang trang mới, để
  // lại chỉ khiến thanh "Đã chọn N" đếm những dòng người dùng không còn nhìn thấy.
  const handleCustomerSearch = (value) => {
    setSelectedCustomerKeys([]);
    customersTable.handleSearch(value);
  };

  const handleCustomerTableChange = (nextPagination) => {
    setSelectedCustomerKeys([]);
    customersTable.handleTableChange(nextPagination);
  };

  // Đang lọc mà bấm "Gỡ tất cả" thì rất dễ hiểu nhầm là chỉ gỡ mấy dòng đang thấy, nên
  // khóa lại cho tới khi bỏ tìm kiếm. Đơn vị chưa có khách nào thì cũng không có gì để gỡ.
  const detachAllDisabled =
    !customersTable.pagination.total || !!customersTable.searchText;

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);

    // Lazy load collections khi chuyển sang tab
    if (key === "collections" && collectionsTable.data.length === 0) {
      collectionsTable.loadData();
    }
  };


  const handleDownloadSampleFile = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      
      const exportUrl = baseUrl ? `${baseUrl}/api/v1/datamanagement/sample-customers` : `/api/v1/datamanagement/sample-customers`;

      const hasAccessToken = settings?.getCookie("access_token");
      // Gọi API tải file mẫu
      const response = await fetch(exportUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hasAccessToken}`,
        }
      });
      
      if (!response.ok) throw new Error('Lỗi tải file mẫu!');
      
      // Tạo blob từ response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Tạo link download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_khach_hang.xlsx';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      throw error; // Component sẽ hiển thị lỗi
    }
  };
  
  const handleUploadSuccess = async ({ files, is_asset_create }) => {
    try {
      if (!files || files.length === 0) {
        message.warning('Vui lòng chọn 1 file để tải lên!');
        return;
      }

      // Mỗi lần chỉ nhập một file: UploadModal cũng chỉ giữ lại file chọn sau cùng.
      const file = files[0].originFileObj;
      
      if (!file) {
        message.error('Không tìm thấy file để upload!');
        return;
      }

      await customerAPIWithErrorHandling.importCustomers(file, [id], null, is_asset_create);

      message.success('Tải lên thành công, dữ liệu đang được xử lý!');

      // Đóng modal
      setModalVisible(false);
      customersTable.loadData();
      fetchOrganizationInfo(id);
      fetchTotalCollection(id);
      fetchCustomerCount(id);

    } catch (error) {
      console.error('Upload error:', error);
      message.error('Tải lên thất bại, vui lòng thử lại!');
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrganizationInfo(id);
      customersTable.loadData();
      fetchTotalCollection(id);
      fetchCustomerCount(id);
    }
  }, [id]);

  const handleBack = () => {
    navigate("/organizations");
  };

  // Customer Table columns
  const customerColumns = [
    {
      title: "STT",
      key: "index",
      width: 50,
      fixed: "left",
      render: (_, record, index) =>
        (customersTable.pagination.current - 1) *
          customersTable.pagination.pageSize +
        index +
        1,
    },
    // {
    //   title: "Mã KH",
    //   key: "code",
    //   width: "15%",
    //   render: (_, record) => (
    //     <Tag color="blue" style={{ fontWeight: 500 }}>
    //       {record.code}
    //     </Tag>
    //   ),
    // },
    {
      title: "Khách hàng",
      key: "name",
      width: 200,
      // ellipsis: true,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: "4px" }}>
            {record.name || `Khách hàng ${record.code}`}
          </div>
        </div>
      ),
    },
    // {
    //   title: "Người đại diện",
    //   dataIndex: "represent_name",
    //   key: "represent_name",
    //   width: 200,
    // },
    {
      title: "Số điện thoại",
      key: "phone",
      width: 150,
      render: (_, record) => (
        <Space>
          <PhoneOutlined style={{ color: "#128555" }} />
          <Text>{record.phone || "-"}</Text>
        </Space>
      ),
    },
    // {
    //     title: "Số nhà",
    //     dataIndex: "sonha",
    //     key: "sonha",
    //     align: "center",
    //     width: 120,
    //     render: (_, record) => (
    //         <div>{record?.sonha ?? '_'}</div>
    //     ),
    // },
    // {
    //     title: "Địa chỉ",
    //     dataIndex: "address",
    //     key: "address",
    //     // align: "center",
    //     width: 250,
    // },
    // {
    //   title: "Trạng thái",
    //   key: "status",
    //   width: 180,
    //   render: (_, record) => (
    //     <Tag color={record.status === true ? "green" : "default"}>
    //       {record.status === true ? "Hoạt động" : "Ngừng"}
    //     </Tag>
    //   ),
    // },
    {
      title: "Thao tác",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <RowActions
          actions={[
            {
              key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view",
              onClick: () => navigate(`/customers/information/${record.id}`),
            },
            {
              key: "detach", label: "Gỡ khỏi đơn vị", icon: <DisconnectOutlined />, tone: "danger",
              onClick: () => confirmRemoveCustomer(record),
            },
          ]}
        />
      ),
    },
  ];
  
  const handleCusInfo = (record) => {
    navigate(`/customers/information/${record.customer_id}`);
  };

  // Cột cho bảng chọn khách hàng khi thêm hàng loạt.
  // Không có cột Trạng thái: danh sách này backend đã lọc sẵn khách đang hoạt động nên
  // cột đó lúc nào cũng cùng một giá trị, chỉ tốn chỗ.
  const bulkCustomerColumns = [
    {
      title: "Mã KH",
      dataIndex: "code",
      key: "code",
      width: 150,
      render: (code) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: "Tên khách hàng",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name || `Khách hàng ${record.code}`}</div>
          {record.represent_name && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.represent_name}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Số điện thoại",
      dataIndex: "phone",
      key: "phone",
      width: 150,
      render: (phone) => (
        <Space size={4}>
          <PhoneOutlined style={{ color: "#128555" }} />
          <Text>{phone || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Địa chỉ",
      dataIndex: "address",
      key: "address",
      width: 220,
      render: (address) => address || "-",
    },
  ];

  // Collections Table columns
  const collectionColumns = [
    {
      title: "STT",
      key: "index",
      width: "5%",
      render: (_, record, index) =>
        (collectionsTable.pagination.current - 1) *
          collectionsTable.pagination.pageSize +
        index +
        1,
    },
    {
      title: "Mã HD",
      key: "code",
      width: "10%",
      render: (_, record) => <Tag>{record.code}</Tag>,
    },
    {
      title: "Khách hàng",
      key: "customer",
      width: "18%",
      render: (_, record) => (
        <div onClick={() => handleCusInfo(record)}
            style={{
                cursor: "pointer",
                color: "#0070D7",
            }}>
          <div style={{ fontWeight: 500 }}>
            {record.customer?.name || "_"}
          </div>
          
          {/* {record.customer?.phone && (
            <Text type="secondary" style={{ fontSize: "13px" }}>
              {record.customer?.phone}
            </Text>
          )} */}
          {record.customer?.phone && (
              <Text type="secondary" className="text-xs">
                  {[record.customer.represent_name, record.customer.phone]
                  .filter(Boolean)
                  .join(" - ")}
              </Text>
          )}
        </div>
      ),
    },
    {
      // Tài sản (số nhà) gắn với khoản thu. Ưu tiên tên tài sản đang lưu, thiếu thì
      // lấy charge_target_number ghi thẳng trên khoản thu — khoản thu loại CUSTOMER
      // hoặc dữ liệu cũ không có asset_id nên không phải dòng nào cũng có tài sản.
      title: "Tài sản",
      key: "asset",
      width: "13%",
      render: (_, record) => {
        const tenTaiSan = record.asset?.name || record.charge_target_number;

        if (!tenTaiSan) {
          return <Text type="secondary">_</Text>;
        }

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{tenTaiSan}</div>
            {record.asset?.code && (
              <Text type="secondary" className="text-xs">
                {record.asset.code}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Đợt thu",
      key: "collection",
      width: "17%",
      render: (_, record) => (
        <div style={{ fontWeight: 500 }}>{record.collection?.name || "_"}</div>
      ),
    },
    {
      title: "Tổng cộng",
      key: "total",
      width: "13%",
      align: "right",
      render: (_, record) => {
        const total = Number(record.amount_due || 0);
        return (
          <Text strong style={{ color: "#0070D7" }}>
            {total.toLocaleString("vi-VN")} ₫
          </Text>
        );
      },
    },
    {
      title: "Đã thanh toán",
      key: "amount_paid",
      width: "12%",
      align: "right",
      render: (_, record) => {
        const total = Number(record.amount_paid || 0);
        return (
          <Text strong style={{ color: "#A8681E" }}>
            {total.toLocaleString("vi-VN")} ₫
          </Text>
        );
      },
    },
    {
      title: "Trạng thái",
      key: "status",
      width: "12%",
      render: (_, record) => (
        <Tag color={FEE_STATUS_COLORS[record.status] || "default"}>
          {FEE_STATUS_LABELS[record.status] || record.status}
        </Tag>
      ),
    },
  ];

  if (loadingInfo && !organization) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" tip="Đang tải thông tin đơn vị..." />
      </div>
    );
  }

  if (!organization) {
    return (
      <div style={containerStyle}>
        <Alert
          message="Không tìm thấy thông tin"
          description="Đơn vị có thể đã bị xóa hoặc ID không hợp lệ."
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={handleBack}>
              Quay lại danh sách
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Breadcrumb */}
      <Breadcrumb
        separator=">"
        style={{ marginBottom: "14px" }}
        items={[
          {
            href: "/",
            title: (
              <>
                <HomeOutlined /> Trang chủ
              </>
            ),
          },
          {
            href: "/organizations",
            title: (
              <>
                <TeamOutlined /> Đơn vị
              </>
            ),
          },
          {
            title: (
              <>
                <InfoCircleOutlined /> Chi tiết
              </>
            ),
          },
        ]}
      />

      {/* Header */}
      <div style={headerStyle}>
        <div style={userInfoStyle}>
          <div style={{ flex: 1 }}>
            <Title level={2} style={titleStyle}>
              {organization.name}
            </Title>
            <Text type="secondary" style={codeStyle}>
              Mã Đơn Vị:{" "}
              <span style={codeValueStyle}>{organization.code}</span>
            </Text><br />
            <Text type="secondary" style={codeStyle}>
              Chi tiết:{" "}
              <span>{organization.description}</span>
            </Text>
          </div>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} size="default">
          Quay lại
        </Button>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            icon={<TeamOutlined />}
            tone="brand"
            label="Tổng khách hàng"
            value={totalCustomers}
            loading={customersTable.loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            icon={<CheckCircleOutlined />}
            tone="success"
            label="Đã thanh toán"
            value={formatAmount(totalCollections.total_paid)}
            loading={loadingStats}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            icon={<ClockCircleOutlined />}
            tone="warning"
            label="Chưa thanh toán"
            value={formatAmount(totalCollections.total_unpaid)}
            loading={loadingStats}
          />
        </Col>
      </Row>

      {/* Tabs Card */}
      <Card style={cardStyle}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "customers",
              label: (
                <Space>
                  <UserOutlined />
                  <span>
                    {/* Khách hàng ({customersTable.pagination.total || 0}) */}
                    Khách hàng
                  </span>
                </Space>
              ),
              children: (
                <>
                  <Space
                    style={{
                      marginBottom: 16,
                      width: "100%",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                    }}
                  >
                    <Input.Search
                      placeholder="Tìm kiếm khách hàng..."
                      allowClear
                      onSearch={handleCustomerSearch}
                      style={{ width: 300 }}
                      prefix={<SearchOutlined />}
                    />
                    <Space wrap>
                      {selectedCustomerKeys.length > 0 && (
                        <>
                          <Text type="secondary">
                            Đã chọn {selectedCustomerKeys.length}
                          </Text>
                          <Button
                            danger
                            icon={<DisconnectOutlined />}
                            loading={bulkDetaching}
                            onClick={handleBulkDetach}
                          >
                            Gỡ đã chọn
                          </Button>
                        </>
                      )}
                      <Tooltip
                        title={
                          customersTable.searchText
                            ? "Xóa ô tìm kiếm trước đã — nút này gỡ toàn bộ đơn vị, không riêng kết quả đang lọc"
                            : ""
                        }
                      >
                        {/* Nút disabled không bắn sự kiện chuột nên Tooltip không hiện —
                            phải treo lên thẻ bọc, đúng lúc người dùng cần lời giải thích
                            nhất thì mới có. */}
                        <span style={{ display: "inline-block", cursor: detachAllDisabled ? "not-allowed" : undefined }}>
                          <Button
                            danger
                            icon={<DisconnectOutlined />}
                            loading={bulkDetaching}
                            disabled={detachAllDisabled}
                            style={detachAllDisabled ? { pointerEvents: "none" } : undefined}
                            onClick={handleDetachAll}
                          >
                            Gỡ tất cả
                          </Button>
                        </span>
                      </Tooltip>
                      <ExportExcelButton
                        url={`/api/v1/organizations/${id}/customers/export`}
                        params={customersTable.searchText ? { search: customersTable.searchText } : {}}
                        fileName="khach_hang_don_vi.xlsx"
                        type="default"
                      />
                      <Button
                        type="primary"
                        icon={<TeamOutlined />}
                        onClick={handleBulkAdd}
                      >
                        Thêm hàng loạt
                      </Button>
                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => setModalVisible(true)}
                      >
                        Import
                      </Button>
                    </Space>
                  </Space>
                  <Table
                    columns={customerColumns}
                    dataSource={customersTable.data}
                    rowKey="id"
                    loading={customersTable.loading}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    rowSelection={{
                      selectedRowKeys: selectedCustomerKeys,
                      onChange: setSelectedCustomerKeys,
                      // Cột STT đã fixed left nên cột tích chọn phải fixed theo, không thì
                      // cuộn ngang là checkbox trôi khỏi khối đứng yên.
                      fixed: true,
                    }}
                    pagination={{
                      ...customersTable.pagination,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} của ${total} khách hàng`,
                      pageSizeOptions: ["10", "15", "20", "50"],
                    }}
                    onChange={handleCustomerTableChange}
                  />
                </>
              ),
            },
            {
              key: "collections",
              label: (
                <Space>
                  <FileTextOutlined />
                  <span>
                    {/* Phiếu thu ({collectionsTable.pagination.total || 0}) */}
                    Phiếu thu
                  </span>
                </Space>
              ),
              children: (
                <>
                  <Space
                    style={{
                      marginBottom: 16,
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Input.Search
                      placeholder="Tìm theo mã HD, khách hàng, số nhà, đợt thu..."
                      allowClear
                      onSearch={collectionsTable.handleSearch}
                      style={{ width: 300 }}
                      prefix={<SearchOutlined />}
                    />
                    <ExportExcelButton
                      url={`/api/v1/organizations/${id}/collections/export`}
                      params={collectionsTable.searchText ? { search: collectionsTable.searchText } : {}}
                      fileName="phieu_thu_don_vi.xlsx"
                      type="default"
                    />
                  </Space>
                  <Table
                    columns={collectionColumns}
                    dataSource={collectionsTable.data}
                    rowKey="id"
                    loading={collectionsTable.loading}
                    // scroll={{ x: 1000 }}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    pagination={{
                      ...collectionsTable.pagination,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} của ${total} phiếu thu`,
                      pageSizeOptions: ["10", "15", "20", "50"],
                    }}
                    onChange={collectionsTable.handleTableChange}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* Ngăn kéo thêm khách hàng hàng loạt */}
      <Drawer
        title={
          <Space>
            <TeamOutlined style={{ color: brand.accent }} />
            <span>
              Thêm khách hàng hàng loạt
              {selectedCustomerIds.length > 0 && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  Đã chọn: {selectedCustomerIds.length}
                </Tag>
              )}
            </span>
          </Space>
        }
        placement="right"
        width="min(880px, 100vw)"
        open={isBulkAddModalVisible}
        onClose={() => {
          setIsBulkAddModalVisible(false);
          setSelectedCustomerIds([]);
          setBulkSearchText("");
        }}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button
              onClick={() => {
                setIsBulkAddModalVisible(false);
                setSelectedCustomerIds([]);
                setBulkSearchText("");
              }}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              onClick={handleBulkAddSubmit}
              disabled={selectedCustomerIds.length === 0}
            >
              {`Thêm ${selectedCustomerIds.length} khách hàng`}
            </Button>
          </div>
        }
      >
        <div style={{ marginTop: "16px" }}>
          <Space
            style={{
              marginBottom: 16,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Input.Search
              placeholder="Tìm kiếm khách hàng..."
              allowClear
              // value={bulkSearchText}
              onSearch={handleBulkSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  handleBulkSearch('');
                }
              }}
              style={{ width: 400 }}
              prefix={<SearchOutlined />}
            />
            <Space>
              {/* Chọn thêm trang đang xem, không xóa lựa chọn ở các trang trước */}
              <Button
                size="small"
                onClick={() =>
                  setSelectedCustomerIds((prev) => [
                    ...new Set([...prev, ...bulkCustomers.map((c) => c.id)]),
                  ])
                }
              >
                Chọn cả trang
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedCustomerIds([])}
                disabled={selectedCustomerIds.length === 0}
              >
                Bỏ chọn
              </Button>
              <Button
                size="small"
                type="primary"
                ghost
                loading={bulkSubmitting}
                disabled={!bulkPagination.total}
                onClick={handleBulkAddAll}
              >
                Thêm tất cả ({bulkPagination.total})
              </Button>
            </Space>
          </Space>

          <Table
            columns={bulkCustomerColumns}
            dataSource={bulkCustomers}
            rowKey="id"
            size="small"
            loading={loadingBulkCustomers}
            locale={{
              emptyText: bulkSearchText
                ? "Không tìm thấy khách hàng phù hợp"
                : "Không còn khách hàng nào để thêm",
            }}
            rowSelection={{
              selectedRowKeys: selectedCustomerIds,
              onChange: setSelectedCustomerIds,
              // Giữ lựa chọn khi sang trang khác — danh sách chia trang ở backend nên
              // các dòng đã chọn không còn nằm trong dataSource của trang đang xem.
              preserveSelectedRowKeys: true,
            }}
            pagination={{
              ...bulkPagination,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} khách hàng`,
            }}
            onChange={(nextPagination) =>
              fetchBulkCustomers({ page: nextPagination.current })
            }
          />

          <Alert
            message="Lưu ý"
            description="Danh sách chỉ hiển thị khách hàng chưa thuộc đơn vị này. Lựa chọn được giữ khi bạn chuyển trang hoặc đổi từ khóa tìm kiếm."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      </Drawer>


      {modalVisible &&
        <UploadModal
          group={true}
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={handleUploadSuccess}
          title="Nhập khách hàng từ Excel"
          acceptedFileTypes=".xlsx,.xls,.csv"
          maxFileSize={10}
          multiple={false}
          showSampleDownload={true}
          sampleFileName="template_khach_hang.xlsx"
          onDownloadSample={handleDownloadSampleFile}
          showAssetCreate={true}
        />
      }
    </div>
  );
}